import * as Lexxy from "@37signals/lexxy"
import { $getRoot, FORMAT_ELEMENT_COMMAND } from "lexical"

// Lexxy extension that adds a text alignment dropdown (left/center/right/justify)
// to the toolbar. Lexxy does not support text-align natively (cf. upstream PR
// #513 closed on 2026-03-16, official Basecamp position: "outside Lexxy's core
// feature set").
//
// Opt-in activation via `data-text-align` on `<lexxy-editor>`.
//
// Persistence: Lexical applies `style="text-align: …"` on blocks via
// FORMAT_ELEMENT_COMMAND, but Lexxy's DOMPurify strips these styles at
// serialization time. We hook the `cachedValue` setter (public custom-element
// property) to re-inject a `text-{align}` class read from Lexical state via
// getFormatType() — preserved by ActionText's sanitizer on the Rails side.
// Remove this hack if upstream PR #1002 (`allowedStyles`) is merged.
//
// PEER DEPENDENCIES (host app must provide):
//   - "@37signals/lexxy" (peer of dlnk_ui — only required when this extension is used)
//   - "lexical" (transitive via @37signals/lexxy)
//
// USAGE (host app application.js):
//   import * as Lexxy from "@37signals/lexxy"
//   import { TextAlignExtension } from "dlnk_ui/dlnk_text_align_extension"
//   Lexxy.config({ global: { extensions: [ TextAlignExtension ] } })
//
// Then opt in per-editor via `<lexxy-editor data-text-align="true">`. Optional
// localized labels via `data-text-align-labels='{"title":"…","left":"…",…}'`.

const SVG_NS = "http://www.w3.org/2000/svg"

const ALIGN_CLASS = {
  left:    "text-left",
  center:  "text-center",
  right:   "text-right",
  justify: "text-justify"
}
const ALIGN_NAMES = Object.keys(ALIGN_CLASS)
const ALIGN_CLASSES_LIST = Object.values(ALIGN_CLASS)

const ICON_PATHS = {
  left:      "M3 4h14M3 8h10M3 12h14M3 16h8",
  center:    "M3 4h14M5 8h10M3 12h14M6 16h8",
  right:     "M3 4h14M7 8h10M3 12h14M9 16h8",
  justify:   "M3 4h14M3 8h14M3 12h14M3 16h14",
  textAlign: "M3 4h14M3 8h10M3 12h14M3 16h10"
}

// Lexical/Lexxy node types that don't emit HTML — skip when mapping HTML to
// Lexical state (typo "provisonal" reproduced from upstream runtime to match
// the exact value of child.getType()).
const NO_HTML_OUTPUT_TYPES = new Set([ "provisonal_paragraph" ])

// English fallback (Lexxy default language). These labels are only used if
// `<lexxy-editor>` has no `data-text-align-labels` attribute. Host apps should
// pass localized labels via that attribute (e.g., `tag.lexxy_editor data: { text_align_labels: {...}.to_json }`).
const DEFAULT_LABELS = {
  title:   "Text alignment",
  left:    "Align left",
  center:  "Center",
  right:   "Align right",
  justify: "Justify"
}

export class TextAlignExtension extends Lexxy.Extension {
  get enabled() {
    return this.editorElement.supportsRichText && this.#optedIn
  }

  initializeToolbar(toolbar) {
    if (toolbar.querySelector("[data-text-align-dropdown]")) return
    this.#insertBeforeOverflow(toolbar, this.#buildDropdown())
    this.#patchCachedValueSetter()
  }

  // Insert before the spacer (which pushes the overflow menu to flex-end) so
  // our button stays in the normal button flow. Otherwise it ends up after the
  // overflow menu and floats to the right, outside the visual container.
  #insertBeforeOverflow(toolbar, dropdown) {
    const spacer = toolbar.querySelector(".lexxy-editor__toolbar-spacer")
    const overflow = toolbar.querySelector(".lexxy-editor__toolbar-overflow")
    const anchor = spacer || overflow
    if (anchor) anchor.before(dropdown)
    else toolbar.appendChild(dropdown)
  }

  // --- Toolbar dropdown ---

  #buildDropdown() {
    const labels = this.#labels
    const details = document.createElement("details")
    details.className = "lexxy-editor__toolbar-dropdown"
    details.setAttribute("name", "lexxy-dropdown")
    // Different attribute from the `data-text-align` placed on `<lexxy-editor>`
    // for opt-in: avoids polluting the editor-side or toolbar-side selector.
    details.setAttribute("data-text-align-dropdown", "")

    const summary = document.createElement("summary")
    summary.className = "lexxy-editor__toolbar-button"
    summary.title = labels.title
    summary.appendChild(buildIcon("textAlign"))
    details.appendChild(summary)

    const dropdown = document.createElement("div")
    dropdown.className = "lexxy-editor__toolbar-dropdown-content"

    const list = document.createElement("div")
    list.className = "lexxy-text-align__list"
    for (const name of ALIGN_NAMES) {
      list.appendChild(this.#buildButton(name, labels[name], details))
    }
    dropdown.appendChild(list)
    details.appendChild(dropdown)
    return details
  }

  #buildButton(name, label, details) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "lexxy-text-align__item"
    button.title = label
    button.appendChild(buildIcon(name))

    const text = document.createElement("span")
    text.textContent = label
    button.appendChild(text)

    // Dispatch the LexicalCommand (object) directly via editor.dispatchCommand.
    // The `<lexxy-toolbar>` `data-command="..."` mechanism only works for
    // commands that Lexxy explicitly registers as strings in
    // CommandDispatcher#COMMANDS (bold, italic, etc.). FORMAT_ELEMENT_COMMAND
    // is not in that list — we must pass the command object identifier.
    button.addEventListener("click", () => {
      this.editorElement.editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, name)
      details.open = false
    })
    return button
  }

  // --- cachedValue setter patch ---

  // Lexxy strips `style="text-align: …"` via DOMPurify before writing to
  // `cachedValue`. We intercept the setter to re-inject a CSS alignment class
  // based on the Lexical format of each root block.
  #patchCachedValueSetter() {
    const element = this.editorElement
    const editor = element.editor
    if (!editor) return

    const descriptor = Object.getOwnPropertyDescriptor(element, "cachedValue")
    if (!descriptor) {
      // Lexxy probably renamed/refactored the `cachedValue` property in a
      // later version. The setter will no longer be invoked: alignment classes
      // will not be injected into the submitted HTML, and the user silently
      // loses alignment on every save. We turn this silent regression into an
      // observable signal. See upstream PR #1002 (allowedStyles) which will
      // retire this hack.
      console.error("[text-align] <lexxy-editor>.cachedValue not found — alignment persistence is broken. Likely upstream Lexxy change; check the installed version.")
      return
    }
    if (descriptor.configurable === false) return

    let internal = element.cachedValue
    Object.defineProperty(element, "cachedValue", {
      configurable: true,
      enumerable: true,
      get() { return internal },
      set(html) {
        internal = html ? injectAlignmentClasses(html, editor) : html
      }
    })
  }

  // --- Helpers ---

  get #optedIn() {
    return this.editorElement.hasAttribute("data-text-align")
  }

  get #labels() {
    const raw = this.editorElement.dataset.textAlignLabels
    if (!raw) return DEFAULT_LABELS
    try {
      return { ...DEFAULT_LABELS, ...JSON.parse(raw) }
    } catch {
      return DEFAULT_LABELS
    }
  }
}

// Build an SVG icon via createElementNS — pure DOM API, no innerHTML.
function buildIcon(name) {
  const svg = document.createElementNS(SVG_NS, "svg")
  svg.setAttribute("viewBox", "0 0 20 20")
  svg.setAttribute("class", "lexxy-editor__toolbar-icon")
  svg.setAttribute("aria-hidden", "true")
  const path = document.createElementNS(SVG_NS, "path")
  path.setAttribute("d", ICON_PATHS[name])
  path.setAttribute("stroke", "currentColor")
  path.setAttribute("stroke-width", "2")
  path.setAttribute("stroke-linecap", "round")
  path.setAttribute("fill", "none")
  svg.appendChild(path)
  return svg
}

function injectAlignmentClasses(html, editor) {
  const body = new DOMParser().parseFromString(html, "text/html").body
  const alignments = readAlignments(editor, body.children.length)

  // Sweep stale alignment classes on every block before re-applying. Without
  // this, removing alignment in the editor (Lexical reverts to format "")
  // leaves a previously-injected `text-*` class on the block.
  for (const element of body.children) {
    element.classList.remove(...ALIGN_CLASSES_LIST)
  }

  for (const { index, align } of alignments) {
    const element = body.children[index]
    if (!element) continue
    element.classList.add(ALIGN_CLASS[align])
  }
  return body.innerHTML
}

// Walk root's children via Lexical public APIs (getChildren, getFormatType)
// instead of internal node maps. Returns [{ index, align }] for each block
// that has an alignment, where `index` matches the position in the rendered
// HTML (skipping nodes that don't produce HTML output).
function readAlignments(editor, htmlElementCount) {
  const alignments = []
  if (!editor) return alignments

  editor.getEditorState().read(() => {
    let htmlIndex = 0
    for (const child of $getRoot().getChildren()) {
      if (NO_HTML_OUTPUT_TYPES.has(child.getType())) continue

      const align = child.getFormatType?.()
      if (align && ALIGN_CLASS[align]) {
        alignments.push({ index: htmlIndex, align })
      }
      htmlIndex++
    }

    // Consistency check: if the HTML/Lexical mapping is offset, abort to avoid
    // applying alignment to the wrong block.
    if (htmlIndex !== htmlElementCount) {
      console.warn("[text-align] Lexical/HTML index mismatch:", htmlIndex, "vs", htmlElementCount)
      alignments.length = 0
    }
  })

  return alignments
}
